import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Save as SaveIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon
} from '@mui/icons-material';
import { getAllUserProfiles, updateUserProfile, NTRP_LEVELS, getReportedClients, getApprovedCoachUids, COACHES } from '../utils/firebaseUtils';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../utils/adminUtils';

const AdminUsersPage = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editing, setEditing] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockUser, setBlockUser] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockingId, setBlockingId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [reportedClients, setReportedClients] = useState([]);
  const [reportedLoading, setReportedLoading] = useState(false);
  const [coachUids, setCoachUids] = useState(new Set());

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const [list, approvedUids] = await Promise.all([getAllUserProfiles(), getApprovedCoachUids()]);
      setUsers(list);
      setCoachUids(new Set([...COACHES.map((c) => c.uid), ...(approvedUids || [])]));
    } catch (err) {
      console.error(err);
      setError('Nepavyko įkelti vartotojų');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadReportedClients = async () => {
    setReportedLoading(true);
    try {
      const [list, allProfiles] = await Promise.all([getReportedClients(), getAllUserProfiles()]);
      const emailToProfile = new Map();
      for (const p of allProfiles) {
        const k = (p.email || '').toLowerCase().trim();
        if (k) emailToProfile.set(k, p);
      }
      const enriched = list.map((rc) => {
        const key = (rc.clientEmail || '').toLowerCase().trim();
        const profile = key ? emailToProfile.get(key) : null;
        return {
          ...rc,
          profile: profile || null,
          fullName: profile?.fullName ?? rc.clientName,
          userId: profile?.userId,
          id: profile?.id ?? rc.id
        };
      });
      setReportedClients(enriched);
    } catch (err) {
      console.error(err);
      setError('Nepavyko įkelti raportuotų klientų');
    } finally {
      setReportedLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) loadReportedClients();
  }, [activeTab]);

  const startEdit = (user) => {
    setEditing({
      userId: user.userId,
      fullName: user.fullName ?? '',
      email: user.email ?? '',
      phoneNumber: user.phoneNumber ?? '',
      ntrpLevel: user.ntrpLevel ?? '',
      ntrpInfo: user.ntrpInfo ?? ''
    });
    setExpandedId(user.id);
  };

  const handleSave = async () => {
    if (!editing.userId) return;
    setSavingId(editing.userId);
    setError('');
    try {
      await updateUserProfile(editing.userId, {
        fullName: editing.fullName.trim(),
        email: editing.email.trim(),
        phoneNumber: editing.phoneNumber.trim(),
        ntrpLevel: editing.ntrpLevel,
        ntrpInfo: editing.ntrpInfo.trim()
      });
      await loadUsers();
      if (activeTab === 1) await loadReportedClients();
      setEditing({});
      setExpandedId(null);
    } catch (err) {
      console.error(err);
      setError('Nepavyko išsaugoti');
    } finally {
      setSavingId(null);
    }
  };

  const openBlockDialog = (user) => {
    setBlockUser(user);
    setBlockReason(user.blockedReason || '');
    setBlockDialogOpen(true);
  };

  const closeBlockDialog = () => {
    setBlockDialogOpen(false);
    setBlockUser(null);
    setBlockReason('');
  };

  const handleBlockConfirm = async () => {
    if (!blockUser) return;
    setBlockingId(blockUser.userId);
    setError('');
    try {
      await updateUserProfile(blockUser.userId, {
        blocked: true,
        blockedReason: blockReason.trim()
      });
      await loadUsers();
      if (activeTab === 1) await loadReportedClients();
      closeBlockDialog();
    } catch (err) {
      console.error(err);
      setError('Nepavyko užblokuoti');
    } finally {
      setBlockingId(null);
    }
  };

  const handleUnblock = async (user) => {
    setBlockingId(user.userId);
    setError('');
    try {
      await updateUserProfile(user.userId, {
        blocked: false,
        blockedReason: ''
      });
      await loadUsers();
      if (activeTab === 1) await loadReportedClients();
    } catch (err) {
      console.error(err);
      setError('Nepavyko atblokuoti');
    } finally {
      setBlockingId(null);
    }
  };

  const startEditReported = (rc) => {
    if (!rc.profile) return;
    const u = {
      id: rc.profile.id,
      userId: rc.profile.userId,
      fullName: rc.profile.fullName ?? rc.clientName ?? '',
      email: rc.profile.email ?? rc.clientEmail ?? '',
      phoneNumber: rc.profile.phoneNumber ?? rc.clientPhone ?? '',
      ntrpLevel: rc.profile.ntrpLevel ?? '',
      ntrpInfo: rc.profile.ntrpInfo ?? ''
    };
    setEditing({ userId: u.userId, fullName: u.fullName, email: u.email, phoneNumber: u.phoneNumber, ntrpLevel: u.ntrpLevel, ntrpInfo: u.ntrpInfo });
    setExpandedId(`reported-${rc.id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser || !isUserAdmin(currentUser)) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tik administratorius gali peržiūrėti vartotojų sąrašą.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Klientai
        </Typography>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Visi" />
          <Tab label="Raportuoti" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tab: Visi */}
        {activeTab === 0 && (
        <>
        {/* Mobile: one card per user — left: photo+name, right: info inline */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          {users.map((user) => (
            <Paper
              key={user.id}
              sx={{
                mb: 2,
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'space-between',
                  flexWrap: 'wrap'
                }}
                onClick={() => {
                  const next = expandedId === user.id ? null : user.id;
                  setExpandedId(next);
                  if (next !== user.id) setEditing({});
                }}
              >
                {/* Left: photo + name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, minWidth: 0 }}>
                  <Avatar
                    src={user.photoURL}
                    sx={{ width: 44, height: 44, bgcolor: 'primary.main' }}
                  >
                    {user.fullName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.fullName || '—'}
                    </Typography>
                    {coachUids.has(user.userId) && (
                      <Chip label="Treneris" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    )}
                  </Box>
                </Box>
                {/* Right: email, phone, NTRP inline */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                  <Typography variant="body2" component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email ? (
                      <Link href={`mailto:${user.email}`} sx={{ fontSize: 'inherit' }}>{user.email}</Link>
                    ) : (
                      '—'
                    )}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.phoneNumber ? (
                      <Link href={`tel:${user.phoneNumber}`} sx={{ fontSize: 'inherit' }}>{user.phoneNumber}</Link>
                    ) : (
                      '—'
                    )}
                  </Typography>
                  {user.ntrpLevel ? (
                    <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.75rem', fontWeight: 600 }}>
                      {user.ntrpLevel}
                    </Box>
                  ) : (
                    <Typography variant="body2" component="span">—</Typography>
                  )}
                  {coachUids.has(user.userId) && (
                    <Chip label="Treneris" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  )}
                  {user.blocked && (
                    <Chip label="Užblokuotas" size="small" color="error" sx={{ fontSize: '0.7rem' }} />
                  )}
                </Box>
                <IconButton
                  size="small"
                  sx={{ flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = expandedId === user.id ? null : user.id;
                    setExpandedId(next);
                    if (next !== user.id) setEditing({});
                  }}
                >
                  {expandedId === user.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={expandedId === user.id} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2, pt: 0, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                  {expandedId === user.id && Object.keys(editing).length > 0 && editing.userId === user.userId ? (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
                        Redaguoti profilį
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                          label="Vardas ir pavardė"
                          value={editing.fullName}
                          onChange={(e) => setEditing((prev) => ({ ...prev, fullName: e.target.value }))}
                          fullWidth
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                          label="El. paštas"
                          value={editing.email}
                          onChange={(e) => setEditing((prev) => ({ ...prev, email: e.target.value }))}
                          fullWidth
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                          label="Telefonas"
                          value={editing.phoneNumber}
                          onChange={(e) => setEditing((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                          fullWidth
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <FormControl fullWidth size="small">
                          <InputLabel>NTRP lygis</InputLabel>
                          <Select
                            label="NTRP lygis"
                            value={editing.ntrpLevel}
                            onChange={(e) => setEditing((prev) => ({ ...prev, ntrpLevel: e.target.value }))}
                            sx={{ borderRadius: 2 }}
                          >
                            <MenuItem value="">Nepasirinkta</MenuItem>
                            {NTRP_LEVELS.map((level) => (
                              <MenuItem key={level} value={level}>{level}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="NTRP įsivertinimo informacija"
                          value={editing.ntrpInfo}
                          onChange={(e) => setEditing((prev) => ({ ...prev, ntrpInfo: e.target.value }))}
                          fullWidth
                          multiline
                          rows={3}
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={savingId === user.userId ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={(e) => { e.stopPropagation(); handleSave(); }}
                        disabled={savingId !== null}
                        sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}
                      >
                        {savingId === user.userId ? 'Saugoma...' : 'Išsaugoti'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                        {user.ntrpInfo || '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); startEdit(user); }}
                          sx={{ borderRadius: 2 }}
                        >
                          Redaguoti
                        </Button>
                        {user.blocked ? (
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={blockingId === user.userId ? <CircularProgress size={16} /> : <UnblockIcon />}
                            onClick={(e) => { e.stopPropagation(); handleUnblock(user); }}
                            disabled={blockingId !== null}
                            sx={{ borderRadius: 2 }}
                          >
                            Atblokuoti
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={blockingId === user.userId ? <CircularProgress size={16} /> : <BlockIcon />}
                            onClick={(e) => { e.stopPropagation(); openBlockDialog(user); }}
                            disabled={blockingId !== null}
                            sx={{ borderRadius: 2 }}
                          >
                            Blokuoti
                          </Button>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>

        {/* Desktop: table — left: photo+name, right: email, phone, NTRP */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            display: { xs: 'none', md: 'block' }
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Nuotrauka ir vardas</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>El. paštas</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Telefonas</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>NTRP</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Būsena</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <React.Fragment key={user.id}>
                  <TableRow
                    sx={{ '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer' }}
                    onClick={() => {
                      const next = expandedId === user.id ? null : user.id;
                      setExpandedId(next);
                      if (next !== user.id) setEditing({});
                    }}
                  >
                    <TableCell sx={{ width: 48 }}>
                      <IconButton size="small">
                        {expandedId === user.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    {/* Left: photo + name in one cell */}
                    <TableCell sx={{ minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Avatar
                          src={user.photoURL}
                          sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}
                        >
                          {user.fullName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.fullName || '—'}</Typography>
                        {coachUids.has(user.userId) && (
                          <Chip label="Treneris" size="small" color="primary" variant="outlined" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{user.email ? <Link href={`mailto:${user.email}`}>{user.email}</Link> : '—'}</TableCell>
                    <TableCell>{user.phoneNumber ? <Link href={`tel:${user.phoneNumber}`}>{user.phoneNumber}</Link> : '—'}</TableCell>
                    <TableCell>{user.ntrpLevel || '—'}</TableCell>
                    <TableCell>
                      {user.blocked ? (
                        <Chip label="Užblokuotas" size="small" color="error" />
                      ) : coachUids.has(user.userId) ? (
                        <Chip label="Treneris" size="small" color="primary" variant="outlined" />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedId === user.id ? undefined : 'none' }}>
                      <Collapse in={expandedId === user.id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                          {expandedId === user.id && Object.keys(editing).length > 0 && editing.userId === user.userId ? (
                            <>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                Redaguoti profilį
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, maxWidth: 720 }}>
                                <TextField
                                  label="Vardas ir pavardė"
                                  value={editing.fullName}
                                  onChange={(e) => setEditing((prev) => ({ ...prev, fullName: e.target.value }))}
                                  fullWidth
                                  size="small"
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <TextField
                                  label="El. paštas"
                                  value={editing.email}
                                  onChange={(e) => setEditing((prev) => ({ ...prev, email: e.target.value }))}
                                  fullWidth
                                  size="small"
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <TextField
                                  label="Telefonas"
                                  value={editing.phoneNumber}
                                  onChange={(e) => setEditing((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                                  fullWidth
                                  size="small"
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <FormControl fullWidth size="small">
                                  <InputLabel>NTRP lygis</InputLabel>
                                  <Select
                                    label="NTRP lygis"
                                    value={editing.ntrpLevel}
                                    onChange={(e) => setEditing((prev) => ({ ...prev, ntrpLevel: e.target.value }))}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    <MenuItem value="">Nepasirinkta</MenuItem>
                                    {NTRP_LEVELS.map((level) => (
                                      <MenuItem key={level} value={level}>{level}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  label="NTRP įsivertinimo informacija"
                                  value={editing.ntrpInfo}
                                  onChange={(e) => setEditing((prev) => ({ ...prev, ntrpInfo: e.target.value }))}
                                  fullWidth
                                  multiline
                                  rows={3}
                                  size="small"
                                  sx={{ gridColumn: '1 / -1', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                              </Box>
                              <Button
                                variant="contained"
                                startIcon={savingId === user.userId ? <CircularProgress size={20} /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={savingId !== null}
                                sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}
                              >
                                {savingId === user.userId ? 'Saugoma...' : 'Išsaugoti'}
                              </Button>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                NTRP informacija: {user.ntrpInfo || '—'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); startEdit(user); }}
                                  sx={{ borderRadius: 2 }}
                                >
                                  Redaguoti
                                </Button>
                                {user.blocked ? (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="success"
                                    startIcon={blockingId === user.userId ? <CircularProgress size={16} /> : <UnblockIcon />}
                                    onClick={(e) => { e.stopPropagation(); handleUnblock(user); }}
                                    disabled={blockingId !== null}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    Atblokuoti
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    startIcon={blockingId === user.userId ? <CircularProgress size={16} /> : <BlockIcon />}
                                    onClick={(e) => { e.stopPropagation(); openBlockDialog(user); }}
                                    disabled={blockingId !== null}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    Blokuoti
                                  </Button>
                                )}
                              </Box>
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {users.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Dar nėra registruotų vartotojų su profiliais.
          </Typography>
        )}
        </>
        )}

        {/* Tab: Raportuoti */}
        {activeTab === 1 && (
          reportedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : reportedClients.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Nėra raportuotų klientų.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Vardas</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>El. paštas</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Telefonas</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Pranešimai</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Būsena</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportedClients.map((rc) => (
                    <React.Fragment key={rc.id}>
                      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' }, cursor: rc.profile ? 'pointer' : 'default' }}
                        onClick={rc.profile ? () => { const next = expandedId === `reported-${rc.id}` ? null : `reported-${rc.id}`; setExpandedId(next); if (next !== `reported-${rc.id}`) setEditing({}); } : undefined}
                      >
                        <TableCell sx={{ width: 48 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                            {(rc.fullName || rc.clientName || rc.clientEmail || '?')[0].toUpperCase()}
                          </Avatar>
                        </TableCell>
                        <TableCell>{rc.fullName || rc.clientName || '—'}</TableCell>
                        <TableCell>{rc.clientEmail ? <Link href={`mailto:${rc.clientEmail}`}>{rc.clientEmail}</Link> : '—'}</TableCell>
                        <TableCell>{rc.clientPhone ? <Link href={`tel:${rc.clientPhone}`}>{rc.clientPhone}</Link> : '—'}</TableCell>
                        <TableCell>
                          {rc.reports?.length || 0} pranešimų
                          {rc.reports?.[0] && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {rc.reports[0].coachName}: {rc.reports[0].reason?.slice(0, 40)}{rc.reports[0].reason?.length > 40 ? '...' : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {rc.profile?.blocked ? <Chip label="Užblokuotas" size="small" color="error" /> : '—'}
                        </TableCell>
                        <TableCell>
                          {rc.profile ? (
                            <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                              <Button variant="outlined" size="small" onClick={() => startEditReported(rc)} sx={{ borderRadius: 2 }}>Redaguoti</Button>
                              {rc.profile.blocked ? (
                                <Button variant="outlined" size="small" color="success" startIcon={blockingId === rc.profile.userId ? <CircularProgress size={16} /> : <UnblockIcon />}
                                  onClick={() => handleUnblock(rc.profile)} disabled={blockingId !== null} sx={{ borderRadius: 2 }}>Atblokuoti</Button>
                              ) : (
                                <Button variant="outlined" size="small" color="error" startIcon={blockingId === rc.profile.userId ? <CircularProgress size={16} /> : <BlockIcon />}
                                  onClick={() => openBlockDialog(rc.profile)} disabled={blockingId !== null} sx={{ borderRadius: 2 }}>Blokuoti</Button>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Profilio nėra</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      {rc.profile && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedId === `reported-${rc.id}` ? undefined : 'none' }}>
                            <Collapse in={expandedId === `reported-${rc.id}`} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                                {expandedId === `reported-${rc.id}` && editing.userId === rc.profile.userId ? (
                                  <>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Redaguoti profilį</Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, maxWidth: 720 }}>
                                      <TextField label="Vardas ir pavardė" value={editing.fullName} onChange={(e) => setEditing((p) => ({ ...p, fullName: e.target.value }))} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                      <TextField label="El. paštas" value={editing.email} onChange={(e) => setEditing((p) => ({ ...p, email: e.target.value }))} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                      <TextField label="Telefonas" value={editing.phoneNumber} onChange={(e) => setEditing((p) => ({ ...p, phoneNumber: e.target.value }))} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                      <FormControl fullWidth size="small">
                                        <InputLabel>NTRP lygis</InputLabel>
                                        <Select label="NTRP lygis" value={editing.ntrpLevel} onChange={(e) => setEditing((p) => ({ ...p, ntrpLevel: e.target.value }))} sx={{ borderRadius: 2 }}>
                                          <MenuItem value="">Nepasirinkta</MenuItem>
                                          {NTRP_LEVELS.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
                                        </Select>
                                      </FormControl>
                                      <TextField label="NTRP įsivertinimo informacija" value={editing.ntrpInfo} onChange={(e) => setEditing((p) => ({ ...p, ntrpInfo: e.target.value }))} fullWidth multiline rows={3} size="small" sx={{ gridColumn: '1 / -1', '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                    </Box>
                                    <Button variant="contained" startIcon={savingId === rc.profile.userId ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSave} disabled={savingId !== null} sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}>
                                      {savingId === rc.profile.userId ? 'Saugoma...' : 'Išsaugoti'}
                                    </Button>
                                  </>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">NTRP: {rc.profile.ntrpInfo || '—'}</Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {/* Block User Dialog */}
        <Dialog
          open={blockDialogOpen}
          onClose={closeBlockDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle>Blokuoti vartotoją</DialogTitle>
          <DialogContent>
            {blockUser && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {blockUser.fullName || blockUser.email || '—'} nebegalės kurti rezervacijų.
              </Typography>
            )}
            <TextField
              label="Blokavimo priežastis (neprivaloma)"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Pvz.: Mokėjimų vėlavimas, pažeidimai..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeBlockDialog} sx={{ borderRadius: 2 }}>
              Atšaukti
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={blockingId ? <CircularProgress size={20} /> : <BlockIcon />}
              onClick={handleBlockConfirm}
              disabled={blockingId !== null}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              {blockingId ? 'Blokuojama...' : 'Patvirtinti'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminUsersPage;
